import { ApplicationWithId } from "../interfaces/IApplications";
import { CategoryWithId } from "../interfaces/ICategories";
import { ContestWithId } from "../interfaces/IContests";
import { ParticipantWithId } from "../interfaces/IParticipants";
import { PaymentsType } from "../interfaces/enums";
import { PresentationWithId } from "../interfaces/IPresentations";
import {
  findContestById,
  findAllPresentationsByQuery,
  findCategories,
  WhereCondition,
} from "../utils/firestore";

const toNumber = (value: unknown, fallback = 0): number => {
  const num = Number(value);
  return Number.isFinite(num) ? num : fallback;
};

export const calculatePaymentforParticipant = (
  contest: ContestWithId,
  numberOfAppearances: number
): number => {
  const base = toNumber(contest.participantBaseCost);
  const additional = toNumber(contest.participantAdditionalCost);
  const appearances = toNumber(numberOfAppearances);
  const participantPayment = appearances * base + additional;
  return toNumber(participantPayment);
};

export const calculatePaymentForApplication = async (application: ApplicationWithId) => {
  let sum = 0;
  const contest: ContestWithId = await findContestById(application.contestId);

  switch (contest.paymentType) {
    case PaymentsType.PER_PARTICIPANT: {
      const participantPresentationsMap = application.participantPresentationsMap;
      for (const key in participantPresentationsMap) {
        const numberOfAppearances = toNumber(participantPresentationsMap[key]);
        sum += calculatePaymentforParticipant(contest, numberOfAppearances);
      }
      break;
    }
    case PaymentsType.PER_PARTICIPANT_IN_CATEGORY: {
      // Pobierz prezentacje dla aplikacji i kategorie turnieju
      const whereConditions: WhereCondition[] = [
        { field: "applicationId", operator: "==", value: application.id },
      ];
      const [presentations, categories] = await Promise.all([
        findAllPresentationsByQuery(whereConditions, []) as Promise<PresentationWithId[]>,
        findCategories(application.contestId) as Promise<CategoryWithId[]>,
      ]);
      const categoryById = new Map<string, CategoryWithId>(
        categories.map((c) => [c.id as string, c])
      );

      // Dla każdej kategorii policz unikalnych uczestników i pomnóż przez koszt uczestnika w kategorii
      const presentationsByCategory = presentations.reduce((acc, p) => {
        const list = acc.get(p.categoryId) || [];
        list.push(p);
        acc.set(p.categoryId, list);
        return acc;
      }, new Map<string, PresentationWithId[]>());

      Array.from(presentationsByCategory.entries()).forEach(([categoryId, presList]) => {
        const category = categoryById.get(categoryId);
        const participantSet = new Set<string>();
        presList.forEach((p: PresentationWithId) =>
          p.participants.forEach(
            (part: ParticipantWithId) => part.id && participantSet.add(part.id)
          )
        );
        const participantsCount = participantSet.size;
        const participantCost = toNumber(category?.participantCost);
        sum += participantsCount * participantCost;
      });
      break;
    }
    case PaymentsType.PER_PRESENTATION: {
      const whereConditions: WhereCondition[] = [
        { field: "applicationId", operator: "==", value: application.id },
      ];
      const [presentations, categories] = await Promise.all([
        findAllPresentationsByQuery(whereConditions, []) as Promise<PresentationWithId[]>,
        findCategories(application.contestId) as Promise<CategoryWithId[]>,
      ]);
      const categoryById = new Map<string, CategoryWithId>(
        categories.map((c) => [c.id as string, c])
      );

      presentations.forEach((p: PresentationWithId) => {
        const category = categoryById.get(p.categoryId);
        const presentationCost = toNumber(category?.presentationCost);
        sum += presentationCost;
      });
      break;
    }
    default: {
      // Domyślnie zachowanie jak dla PER_PARTICIPANT
      const participantPresentationsMap = application.participantPresentationsMap;
      for (const key in participantPresentationsMap) {
        const numberOfAppearances = toNumber(participantPresentationsMap[key]);
        sum += calculatePaymentforParticipant(contest, numberOfAppearances);
      }
    }
  }

  const applicationWithSum = { ...application, totalCost: toNumber(sum) };
  return applicationWithSum;
};
